import React, { useState } from 'react';
import moment from 'moment';
import { Card, Row, Select, Empty, Tabs, Grid, Tooltip, Col } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import _, { isEmpty, startCase } from 'lodash';
import DualAxisAreaChart from './components/DualAxisAreaChart';
import DatePickerField from './components/DatePickerField';

// Global Styles
import { MenuItemTextColor, TabText } from '../../../../../shared/globalStyling/styledText';
import { colStyle } from '../../../shared/styles/chart';

import { useByDateFilter } from './hooks/ByDateFilter';
import MultipleSelectDropdown from '../MultipleSelectDropdown';

const Frag = React.Fragment;
const { Option } = Select;

const TabPane = Tabs.TabPane;

const SummaryChart = ({
  byDay,
  bySummaryRangeDate,
  setBySummaryRangeDate,
  reportData,
  showTransactions,
  showLocations,
  showKeywords,
  showPrices,
  isGoogleAds,
  locationsKey,
  onChangeLocationDropdown,
  defaultSummaryDates,
  selectedLocations,
  hasTooltip = true,
  defaultYValue = 'clicks',
  defaultXValue = 'impressions',
  currencyFormat,
}) => {
  // map the chart and return the dates with total summary
  let validOptions = ['clicks', 'impressions', 'ctr', 'spend', 'cpc', 'cpm'];

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const transactionOptions = ['conversions', 'revenue', 'roas', 'conversionRate'];
  const keywordsOptions = ['keywords'];
  const locationsOptions = ['locations'];

  const showPricesExclude = ['roas', 'cpc', 'spend'];

  reportData = reportData.sort((a, b) => moment(a.xAxis).unix() - moment(b.xAxis).unix());

  const { rangeKey, setRangeKey, filteredData, startDateEndDate, setCustomDateRange } =
    useByDateFilter(reportData);

  if (showTransactions) {
    validOptions = validOptions.concat(transactionOptions);
  }

  if (showLocations) {
    validOptions = validOptions.concat(locationsOptions);
  }

  if (showKeywords) {
    validOptions = validOptions.concat(keywordsOptions);
  }

  if (!showPrices) {
    _.remove(validOptions, (c) => {
      return showPricesExclude.includes(c);
    });
  }

  const [leftY, setLeftY] = useState(startCase(defaultYValue));
  const [rightY, setRightY] = useState(startCase(defaultXValue));
  const DateSelector = ({ data, screens }) => {
    // map xAxis dates
    return (
      <DatePickerField
        setCustomDateRange={setCustomDateRange}
        data={filteredData}
        defaultPickerValue={startDateEndDate}
        defaultValue={startDateEndDate}
        defaultSummaryDates={defaultSummaryDates}
        value={isEmpty(bySummaryRangeDate) ? startDateEndDate : bySummaryRangeDate}
        onChange={(v) => {
          const dates = [moment(v.dates[0]), moment(v.dates[1])];
          setBySummaryRangeDate(dates);
        }}
        dropDownValue={rangeKey}
        setDropDownValue={setRangeKey}
        has6MonDisables={false}
        screens={screens}
      />
    );
  };

  const YAxisSelector = ({ displayDateSelector, displayRightSelector, data }) => {
    const keys = Object.keys(data[0])
      .filter((el) => validOptions.includes(el))
      .map((option) => {
        return option === 'cpc' ||
          option === 'cpm' ||
          option === 'ctr' ||
          option === 'ecpm' ||
          option === 'roas'
          ? option.toUpperCase()
          : startCase(option);
      });

    return (
      <Row gutter={[8, 8]} justify="space-between" align="middle" between="xs">
        <Col xs={24} md={6} lg={5} xl={6} xxl={6}>
          <Select
            onSelect={(value) => setLeftY(value)}
            style={{
              width: screens.xs === true ? '100%' : '50%',
              marginBottom: '0.4em',
            }}
            defaultValue={leftY}
          >
            {keys
              .filter((op) => op !== rightY)
              .map((option) => (
                <Option key={option} value={option}>
                  <MenuItemTextColor text={option} color="#7dafff" />
                </Option>
              ))}
          </Select>
        </Col>
        <Col
          xs={24}
          md={12}
          lg={13}
          xl={12}
          xxl={12}
          style={{ display: 'flex', justifyContent: 'center', minWidth: 0, flex: 1 }}
        >
          {displayDateSelector && <DateSelector screens={screens} />}
        </Col>
        <Col
          xs={24}
          md={5}
          lg={5}
          xl={6}
          xxl={6}
          style={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}
        >
          {displayRightSelector && (
            <Select
              onSelect={(value) => setRightY(value)}
              style={{ width: '100%', minWidth: 120 }}
              defaultValue={rightY}
            >
              {keys
                .filter((op) => op !== leftY)
                .map((option) => (
                  <Option key={option} value={option}>
                    <MenuItemTextColor text={option} color="#92d5a7" />
                  </Option>
                ))}
            </Select>
          )}
        </Col>
      </Row>
    );
  };

  // if chartdata is equal to empty
  const emptyCharts = isEmpty(reportData);
  if (emptyCharts)
    return (
      <Card
        style={colStyle.container}
        bodyStyle={{ padding: 18 }}
        title={'Summary Chart'}
        extra={
          <Row justify="space-evenly" align="middle" gutter={[16, 16]}>
            {locationsKey && locationsKey.length > 0 && (
              <Col>
                <MultipleSelectDropdown
                  options={locationsKey}
                  onChange={onChangeLocationDropdown}
                />
              </Col>
            )}
            {hasTooltip && (
              <span>
                <Tooltip
                  title={
                    isGoogleAds ? (
                      <span>
                        Data is processed daily and is not in real time. In addition, revenue and
                        transactions are subject to a default 30 day conversion window. Minor
                        discrepancies between this dashboard and the Google Interface are to be
                        expected. You can learn more about conversion windows here.
                        <br />
                        <br />
                        <a
                          href="https://support.google.com/google-ads/answer/3123169?hl=en"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          https://support.google.com/google-ads/answer/3123169?hl=en
                        </a>
                      </span>
                    ) : (
                      <span>
                        Data is processed daily and is not in real time. Google analytics uses data
                        sampling which is designed to speed up reporting. In addition, revenue and
                        transactions are subject to a default 30 day conversion window. Minor
                        discrepancies between this dashboard and the Google Interface are to be
                        expected.
                        <br />
                        <br />
                        Data Sampling - Links to
                        <br />
                        <a href="https://support.google.com/analytics/answer/2637192?hl=en#zippy=%2Cin-this-article">
                          https://support.google.com/analytics/answer/2637192?hl=en#zippy=%2Cin-this-article
                        </a>
                        <br />
                        <br />
                        Conversion windows - Links to
                        <br />
                        <a
                          href="https://support.google.com/google-ads/answer/3123169?hl=en"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          https://support.google.com/google-ads/answer/3123169?hl=en
                        </a>
                      </span>
                    )
                  }
                  arrowPointAtCenter={true}
                  placement="leftBottom"
                >
                  <QuestionCircleOutlined type="question-circle" style={{ margin: '0 auto' }} />
                </Tooltip>
              </span>
            )}
          </Row>
        }
      >
        <Row type="flex" justify="center" align="middle">
          <DateSelector />
        </Row>
        <Row type="flex" justify="center" align="middle">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Row>
      </Card>
    );

  return (
    <div>
      <Card
        style={colStyle.container}
        bodyStyle={{ padding: 18 }}
        title={'Summary Chart'}
        extra={
          <Row justify="space-evenly" align="middle" gutter={[16, 16]}>
            {locationsKey && locationsKey.length > 0 && (
              <Col>
                <MultipleSelectDropdown
                  options={locationsKey}
                  onChange={onChangeLocationDropdown}
                  selectedLocations={selectedLocations}
                />
              </Col>
            )}
            {hasTooltip && (
              <span>
                <Tooltip
                  title={
                    isGoogleAds ? (
                      <span>
                        Data is processed daily and is not in real time. In addition, revenue and
                        transactions are subject to a default 30 day conversion window. Minor
                        discrepancies between this dashboard and the Google Interface are to be
                        expected. You can learn more about conversion windows here.
                        <br />
                        <br />
                        <a
                          href="https://support.google.com/google-ads/answer/3123169?hl=en"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          https://support.google.com/google-ads/answer/3123169?hl=en
                        </a>
                      </span>
                    ) : (
                      <span>
                        Data is processed daily and is not in real time. Google analytics uses data
                        sampling which is designed to speed up reporting. In addition, revenue and
                        transactions are subject to a default 30 day conversion window. Minor
                        discrepancies between this dashboard and the Google Interface are to be
                        expected.
                        <br />
                        <br />
                        Data Sampling - Links to
                        <br />
                        <a href="https://support.google.com/analytics/answer/2637192?hl=en#zippy=%2Cin-this-article">
                          https://support.google.com/analytics/answer/2637192?hl=en#zippy=%2Cin-this-article
                        </a>
                        <br />
                        <br />
                        Conversion windows - Links to
                        <br />
                        <a
                          href="https://support.google.com/google-ads/answer/3123169?hl=en"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          https://support.google.com/google-ads/answer/3123169?hl=en
                        </a>
                      </span>
                    )
                  }
                  arrowPointAtCenter={true}
                  placement="leftBottom"
                >
                  <QuestionCircleOutlined type="question-circle" style={{ margin: '0 auto' }} />
                </Tooltip>
              </span>
            )}
          </Row>
        }
      >
        <Tabs
          destroyInactiveTabPane={true}
          defaultActiveKey="2"
          style={colStyle.cssStyle}
          onChange={(activeKey) => {
            if (activeKey === '4') setLeftY('Clicks');
            if (activeKey !== '4') {
              setLeftY('Impressions');
              setRightY('Transaction Count');
            }
          }}
        >
          {!isEmpty(reportData) ? (
            <TabPane tab={<TabText text={'By Date'} />} key="2">
              <Card bordered={false} bodyStyle={{ padding: 0 }}>
                <Frag>
                  <YAxisSelector
                    displayRightSelector={true}
                    displayDateSelector={true}
                    data={reportData}
                  />
                  <Row style={{ marginBottom: '2%' }} type="flex" justify="center"></Row>
                  {filteredData.length && validOptions.length > 1 && (
                    <DualAxisAreaChart
                      leftY={isEmpty(leftY) ? defaultYValue || '' : leftY}
                      rightY={isEmpty(rightY) ? defaultXValue || '' : rightY}
                      dualChartData={filteredData}
                      currencyFormat={currencyFormat}
                    />
                  )}
                </Frag>

                {(filteredData.length === 0 ||
                  (bySummaryRangeDate &&
                    bySummaryRangeDate.length > 0 &&
                    filteredData.length === 0)) && <Empty />}
              </Card>
            </TabPane>
          ) : null}
          {!isEmpty(byDay) ? (
            <TabPane tab={<TabText text={'Day Of Week'} />} key="3">
              <Card bordered={false} bodyStyle={{ padding: 0 }}>
                {byDay.length === 0 && (
                  <Card bordered={false}>No By Date Chart Is Available At This Time...</Card>
                )}
                {byDay.length && (
                  <Frag>
                    <YAxisSelector
                      displayRightSelector={true}
                      displayDateSelector={false}
                      data={byDay}
                    />
                    <Row style={{ marginBottom: '2%' }} type="flex" justify="center"></Row>
                    <DualAxisAreaChart
                      leftY={isEmpty(leftY) ? defaultYValue : leftY}
                      rightY={isEmpty(rightY) ? defaultXValue : rightY}
                      dualChartData={byDay}
                    />
                  </Frag>
                )}
              </Card>
            </TabPane>
          ) : null}
        </Tabs>
      </Card>
    </div>
  );
};

export default SummaryChart;
